import type { ReactElement, ReactNode } from "react";
import type { ProblemTimelineParams } from "@playbookai/widget-schemas";
import { useFieldEditSession } from "../field-edit-session.js";
import { timelineSummary } from "./presentation.js";

/**
 * How much got exposed while the problem was live. The total is derived
 * (`rate × suspect window`) unless an explicit total is recorded, which is the
 * precedence `computeSuspectWindow` already applies — this panel only surfaces
 * it.
 */
export function ExposurePanel({
  data,
  readOnly,
  onChangeNumber,
  onChangeText,
}: {
  data: ProblemTimelineParams;
  readOnly: boolean;
  onChangeNumber: (
    key: "exposureRatePerDay" | "estimatedExposure",
    raw: string,
  ) => void;
  onChangeText: (key: "exposureUnit" | "exposureBasis", value: string) => void;
}): ReactElement {
  // PB-4923 AC5 — the widget's four root-level scalars. Each is its own merge
  // target, so sizing the exposure never contends with editing a milestone.
  const rateLease = useFieldEditSession("exposureRatePerDay");
  const unitLease = useFieldEditSession("exposureUnit");
  const estimateLease = useFieldEditSession("estimatedExposure");
  const basisLease = useFieldEditSession("exposureBasis");
  const summary = timelineSummary(data);
  const derived =
    summary.days !== null && data.exposureRatePerDay !== undefined
      ? summary.days * data.exposureRatePerDay
      : null;

  return (
    <section className="flex min-w-0 flex-col gap-3 rounded-lg border border-pb-border bg-pb-card-elevated/30 p-3">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-pb-text-muted">
        Exposure estimate
      </h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Rate / day">
          <input
            type="number"
            min={0}
            disabled={readOnly}
            value={data.exposureRatePerDay ?? ""}
            {...rateLease}
            onChange={(event) =>
              onChangeNumber("exposureRatePerDay", event.target.value)
            }
            className={FIELD_CLASS}
          />
        </Field>
        <Field label="Unit">
          <input
            aria-label="Exposure unit"
            disabled={readOnly}
            maxLength={255}
            value={data.exposureUnit ?? ""}
            {...unitLease}
            onChange={(event) =>
              onChangeText("exposureUnit", event.target.value)
            }
            className={FIELD_CLASS}
          />
        </Field>
      </div>
      <div className="border-t border-pb-border pt-3">
        <Field label="Estimated exposure">
          <input
            aria-label="Estimated exposure"
            type="number"
            min={0}
            disabled={readOnly}
            placeholder={derived === null ? "" : String(derived)}
            value={data.estimatedExposure ?? ""}
            {...estimateLease}
            onChange={(event) =>
              onChangeNumber("estimatedExposure", event.target.value)
            }
            className={FIELD_CLASS}
          />
        </Field>
        <p className="mt-1.5 text-dense-1 text-pb-text-muted">
          {summary.exposure === null ? (
            "Add a rate per day, or record the total directly."
          ) : (
            <>
              <strong className="font-semibold text-pb-amber-text">
                ≈{summary.exposure.toLocaleString("en-GB")}{" "}
                {summary.exposureUnit}
              </strong>{" "}
              {data.estimatedExposure === undefined
                ? "from rate × suspect window."
                : "recorded directly — this overrides rate × suspect window."}
            </>
          )}
        </p>
      </div>
      <Field label="Exposure basis">
        <textarea
          aria-label="Exposure basis"
          rows={3}
          maxLength={500}
          disabled={readOnly}
          placeholder="What the estimate is based on"
          value={data.exposureBasis ?? ""}
          {...basisLease}
          onChange={(event) =>
            onChangeText("exposureBasis", event.target.value)
          }
          className={`${FIELD_CLASS} resize-y`}
        />
      </Field>
    </section>
  );
}

const FIELD_CLASS =
  "mt-1 w-full rounded-md border border-pb-control-border bg-pb-card px-2 py-1.5 text-xs text-pb-text";

function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}): ReactElement {
  return (
    <label className="block min-w-0 text-dense-1 text-pb-text-muted">
      {label}
      {children}
    </label>
  );
}
